/**
 * Technical indicator calculations for portfolio dashboard
 * SMA (Simple Moving Average) and RSI (Relative Strength Index)
 */

/**
 * Calculate Simple Moving Average
 * @param {number[]} closes - Array of closing prices (oldest first)
 * @param {number} period - Number of periods (e.g., 14, 50, 200)
 * @returns {(number|null)[]} - Array of SMA values (null where not enough data)
 */
export function calcSMA(closes, period) {
  const result = [];
  for (let i = 0; i < closes.length; i++) {
    if (i < period - 1) {
      result.push(null);
    } else {
      let sum = 0;
      for (let j = i - period + 1; j <= i; j++) sum += closes[j];
      result.push(sum / period);
    }
  }
  return result;
}

/**
 * Calculate Relative Strength Index
 * @param {number[]} closes - Array of closing prices (oldest first)
 * @param {number} period - RSI period (default 14)
 * @returns {(number|null)[]} - Array of RSI values (null where not enough data)
 */
export function calcRSI(closes, period = 14) {
  const result = [];
  if (closes.length < period + 1) {
    return closes.map(() => null);
  }

  // Calculate price changes
  const changes = [];
  for (let i = 1; i < closes.length; i++) {
    changes.push(closes[i] - closes[i - 1]);
  }

  // First average gain/loss using simple average
  let avgGain = 0;
  let avgLoss = 0;
  for (let i = 0; i < period; i++) {
    if (changes[i] >= 0) avgGain += changes[i];
    else avgLoss += Math.abs(changes[i]);
  }
  avgGain /= period;
  avgLoss /= period;

  // First data point has no RSI
  result.push(null); // index 0 (no change available)
  for (let i = 0; i < period - 1; i++) result.push(null);

  // First RSI value
  const rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
  result.push(avgLoss === 0 ? 100 : 100 - (100 / (1 + rs)));

  // Subsequent values using smoothed average
  for (let i = period; i < changes.length; i++) {
    const gain = changes[i] >= 0 ? changes[i] : 0;
    const loss = changes[i] < 0 ? Math.abs(changes[i]) : 0;
    avgGain = (avgGain * (period - 1) + gain) / period;
    avgLoss = (avgLoss * (period - 1) + loss) / period;
    const smoothedRS = avgLoss === 0 ? 100 : avgGain / avgLoss;
    result.push(avgLoss === 0 ? 100 : 100 - (100 / (1 + smoothedRS)));
  }

  return result;
}

/**
 * Process raw candle data into chart-ready format with indicators
 * @param {Object} candles - { t: [], c: [], o: [], h: [], l: [], v: [] }
 * @returns {Object[]} - Array of data points for Recharts
 */
export function processCandles(candles) {
  if (!candles || !candles.t || candles.t.length === 0) return [];

  const closes = candles.c;
  const sma14 = calcSMA(closes, 14);
  const sma50 = calcSMA(closes, 50);
  const sma200 = calcSMA(closes, 200);
  const rsi = calcRSI(closes, 14);

  return candles.t.map((timestamp, i) => ({
    date: new Date(timestamp * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    dateRaw: new Date(timestamp * 1000),
    open: candles.o[i],
    high: candles.h[i],
    low: candles.l[i],
    close: candles.c[i],
    volume: candles.v[i],
    sma14: sma14[i] ? Math.round(sma14[i] * 100) / 100 : null,
    sma50: sma50[i] ? Math.round(sma50[i] * 100) / 100 : null,
    sma200: sma200[i] ? Math.round(sma200[i] * 100) / 100 : null,
    rsi: rsi[i] ? Math.round(rsi[i] * 100) / 100 : null,
  }));
}
